import { useState } from "react";
import { toast } from "sonner";
import { Download, Upload, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errMsg, openBlob } from "@/lib/api";

const CHIPS = [["new", "New - will be imported", "bg-emerald-100 text-emerald-800"], ["existing", "Already exist - skipped", "bg-sky-100 text-sky-800"],
  ["duplicate", "Repeated in file - skipped", "bg-slate-200 text-slate-700"], ["error", "Need fixing", "bg-red-100 text-red-800"]];
const STATUS = Object.fromEntries(CHIPS.map(([k, l, c]) => [k, [l.split(" - ")[0], c]]));

function Issues({ rows, total }) {
  return (
    <div className="border rounded-lg overflow-x-auto mt-3 max-h-96 overflow-y-auto" data-testid="patient-import-issues">
      <Table>
        <TableHeader><TableRow>{["Row", "Patient ID", "Name", "DOB", "Status", "Notes"].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
        <TableBody>{rows.map((r) => (
          <TableRow key={r.row} data-testid={`patient-issue-${r.row}`}>
            <TableCell className="text-xs text-slate-500">{r.row}</TableCell><TableCell className="text-xs">{r.patient_id}</TableCell>
            <TableCell className="text-xs">{r.name}</TableCell><TableCell className="text-xs">{r.dob}</TableCell>
            <TableCell className="text-xs"><span className={`px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS[r.status][1]}`}>{STATUS[r.status][0]}</span></TableCell>
            <TableCell className="text-xs">{r.messages.map((m) => <span key={m} className={`block ${r.status === "error" ? "text-red-700" : "text-slate-600"}`}>{m}</span>)}</TableCell>
          </TableRow>))}</TableBody>
      </Table>
      {total > rows.length && <p className="text-xs text-slate-500 p-2">Showing the first {rows.length} of {total} rows with notes.</p>}
    </div>
  );
}

export function PatientImportCard() {
  const [file, setFile] = useState(null);
  const [check, setCheck] = useState(null);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async (path, f) => { const fd = new FormData(); fd.append("file", f); return (await api.post(path, fd, { timeout: 300000 })).data; };
  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) { return; }
    setBusy(true);
    try { setCheck(await send("/setup-import/patients/preview", f)); setFile(f); setDone(null); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const accept = async () => {
    setBusy(true);
    try { const d = await send("/setup-import/patients/commit", file); setDone(d); toast.success(`${d.imported} patients imported`); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <section className="rounded-xl border bg-white p-4" data-testid="setup-card-patients">
      <div className="flex flex-wrap items-center gap-2">
        <Users className="w-5 h-5 text-brand" /><h3 className="font-semibold mr-auto">Patients</h3>
        <Button size="sm" variant="outline" onClick={() => openBlob("/setup-import/patients/template", "ace-patients-template.xlsx")} data-testid="setup-template-patients"><Download className="w-4 h-4 mr-1" />Download template</Button>
        <label className={`inline-flex items-center h-9 px-3 rounded-md text-sm btn-brand cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid="setup-upload-patients">
          <Upload className="w-4 h-4 mr-1" />{busy && !check ? "Checking..." : "Upload filled file"}<input type="file" accept=".xlsx,.csv" className="hidden" onChange={upload} data-testid="setup-file-patients" /></label>
      </div>
      <p className="text-xs text-slate-500 mt-1">Required: Patient ID / MRN, first name, last name, date of birth. Everything else can be blank. Up to 20,000 patients per file; existing patients are skipped.</p>
      {check && <>
        <div className="flex flex-wrap gap-2 mt-3" data-testid="patient-import-summary">
          <span className="text-xs text-slate-600 self-center">{check.total} rows:</span>
          {CHIPS.map(([k, l, c]) => <span key={k} className={`text-xs px-2 py-1 rounded-full ${c}`} data-testid={`patient-count-${k}`}>{check.counts[k]} {done && k === "new" ? "New - imported" : l}</span>)}
        </div>
        {check.issues.length > 0 && <Issues rows={check.issues} total={check.issues_total} />}
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {done ? <span className="text-sm text-emerald-700 mr-auto" data-testid="patient-import-result">{done.imported} patients imported{done.skipped ? `, ${done.skipped} skipped` : ""}.</span> : <span className="mr-auto text-xs text-slate-500">{check.counts.error ? "Rows that need fixing are left out - fix them in the file and upload it again later." : ""}</span>}
          {!done && <Button size="sm" className="btn-brand" disabled={busy || !check.counts.new} onClick={accept} data-testid="setup-accept-patients">{busy ? "Importing..." : `Accept & import ${check.counts.new}`}</Button>}
          {done && <Button size="sm" variant="ghost" onClick={() => { setCheck(null); setDone(null); }} data-testid="setup-done-patients">Done</Button>}
        </div>
      </>}
    </section>
  );
}
